/**
 * Scheduling Management Page
 *
 * Main page for managing class schedules.
 * Allows Owner/Manager to create schedules, manage classes, rooms, teachers, and run the scheduling algorithm.
 * Pro plan feature.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import Header from '../../components/layout/Header';
import { useOwnerManagerAccess } from '../../hooks/useToolAccess';
import { useSessionAuth } from '../../hooks/useSessionAuth';
import StudioSelectorControl from '../../components/common/StudioSelectorControl';
import SchedulesList from './SchedulesList';
import FeatureGate from '../../components/common/FeatureGate';
import NoToolAccess from '../../components/common/NoToolAccess';

const SchedulingManagement: React.FC = () => {
  const navigate = useNavigate();

  // Get authentication state
  const { isAuthenticated, isLoading: authLoading } = useSessionAuth();

  // Get studio access information (Owner/Manager only, Pro plan required)
  const {
    eligibleStudios,
    hasToolAccess,
    hasSingleStudio,
    isLoading: toolAccessLoading,
  } = useOwnerManagerAccess();

  // Component state
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null);

  // Handle studio selection and access control
  useEffect(() => {
    // Wait for authentication and tool access data to load
    if (authLoading || toolAccessLoading) {
      return;
    }

    // If user is not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Auto-select if user has exactly one eligible studio
    if (hasSingleStudio) {
      const [studio] = eligibleStudios;
      setSelectedStudioId(studio.id);
    }
  }, [
    navigate,
    authLoading,
    isAuthenticated,
    toolAccessLoading,
    hasSingleStudio,
    eligibleStudios,
  ]);

  // Show no access message if user doesn't have required permissions
  if (!authLoading && !toolAccessLoading && !hasToolAccess) {
    return (
      <NoToolAccess
        toolName="Class Scheduling"
        requiredRoles={['owner', 'manager']}
      />
    );
  }

  // Show loading state while checking authentication and access
  if (authLoading || toolAccessLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Header />
      <FeatureGate
        requirement={{ minimumPlan: 'pro' }}
        featureName="Class Scheduling"
        upgradeDescription="Create and manage class schedules using our automated scheduling algorithm. Generate optimized schedules based on teacher availability, room configurations, and class preferences."
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {/* Page Header */}
          <Box mb={4}>
            <Typography variant="h4" component="h1" gutterBottom>
              Class Scheduling
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and manage class schedules using our automated scheduling
              algorithm
            </Typography>
          </Box>

          {/* Studio Selector - Only shows for multiple studios */}
          <Box mb={4}>
            <StudioSelectorControl
              studios={eligibleStudios}
              selectedStudioId={selectedStudioId}
              onStudioChange={setSelectedStudioId}
            />
          </Box>

          {/* Info Alert for Multiple Studios */}
          {eligibleStudios.length > 1 && !selectedStudioId && (
            <Box mb={4}>
              <Alert severity="info" sx={{ maxWidth: 400 }}>
                Please select a studio to manage its schedules.
              </Alert>
            </Box>
          )}

          {/* Content area - only show if studio is selected */}
          {selectedStudioId && <SchedulesList studioId={selectedStudioId} />}
        </Container>
      </FeatureGate>
    </>
  );
};

export default SchedulingManagement;
